import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Icons from '../components/Icons';
import { useStudentStore } from '../store/StudentStore';
import ProgrammeService from '../services/ProgrammeService';
import { formatDate, formatWithOptions } from '../utils/dateFormat';

export default function ProgrammesManagePage() {
  const { students } = useStudentStore();
  const navigate = useNavigate();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);

  const programmeRows = useMemo(() => {
    const enrolmentCounts = new Map();
    students.forEach((student) => {
      (student.enrolments || []).forEach((enrol) => {
        if (!enrol.programme) return;
        enrolmentCounts.set(enrol.programme, (enrolmentCounts.get(enrol.programme) || 0) + 1);
      });
    });

    return programmes.map((programme) => {
      const enrolled = enrolmentCounts.get(programme.name) || programme.enrolled || 0;
      const capacity = programme.capacity || 0;
      const utilisation = capacity ? Math.round((enrolled / capacity) * 100) : 0;
      const seatsRemaining = capacity ? Math.max(capacity - enrolled, 0) : 0;
      return {
        ...programme,
        enrolled,
        utilisation,
        seatsRemaining,
        totalFees:
          (programme.fees?.courseFee || 0) +
          (programme.fees?.eventFee || 0) +
          (programme.fees?.otherFee || 0),
        templateCount: programme.templates?.length || 0,
        documentCount: programme.documents?.length || 0,
        lessonPlanCount: programme.lessonPlans?.length || 0
      };
    });
  }, [students, programmes]);

  const analytics = useMemo(() => {
    const totalProgrammes = programmeRows.length;
    const totalCapacity = programmeRows.reduce((sum, row) => sum + (row.capacity || 0), 0);
    const totalEnrolled = programmeRows.reduce((sum, row) => sum + (row.enrolled || 0), 0);
    const avgUtilisation = totalCapacity ? Math.round((totalEnrolled / totalCapacity) * 100) : 0;
    const fullCount = programmeRows.filter((row) => row.utilisation >= 90 && row.capacity > 0).length;
    const emptyCount = programmeRows.filter((row) => row.enrolled === 0).length;
    const lowUtilisation = programmeRows.filter((row) => row.utilisation > 0 && row.utilisation < 40).length;
    const missingDocs = programmeRows.filter((row) => row.documentCount === 0).length;
    const missingTemplates = programmeRows.filter((row) => row.templateCount === 0).length;
    const missingLessonPlans = programmeRows.filter((row) => row.lessonPlanCount === 0).length;
    const unassignedStaff = programmeRows.filter(
      (row) => !row.staff || row.staff === 'Unassigned'
    ).length;
    const totalFees = programmeRows.reduce((sum, row) => sum + (row.totalFees || 0), 0);

    const now = new Date();
    const soon = new Date();
    soon.setDate(now.getDate() + 30);
    const endingSoon = programmeRows.filter((row) => {
      if (!row.endDate) return false;
      const endDate = new Date(row.endDate);
      if (Number.isNaN(endDate.getTime())) return false;
      return endDate >= now && endDate <= soon;
    });

    const watchlist = programmeRows
      .map((row) => {
        const reasons = [];
        if (row.enrolled === 0) reasons.push('No enrolments');
        if (row.utilisation > 0 && row.utilisation < 40) reasons.push('Low utilisation');
        if (!row.staff || row.staff === 'Unassigned') reasons.push('No staff assigned');
        if (row.documentCount === 0) reasons.push('Missing documents');
        if (row.templateCount === 0) reasons.push('No templates');
        if (row.utilisation >= 90 && row.capacity > 0) reasons.push('At capacity');
        return { row, reasons };
      })
      .filter((item) => item.reasons.length > 0)
      .slice(0, 5);

    return {
      totalProgrammes,
      totalCapacity,
      totalEnrolled,
      avgUtilisation,
      fullCount,
      emptyCount,
      lowUtilisation,
      missingDocs,
      missingTemplates,
      missingLessonPlans,
      unassignedStaff,
      totalFees,
      endingSoon,
      watchlist
    };
  }, [programmeRows]);

  const weekDays = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    const day = start.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    start.setDate(start.getDate() + diff);
    return Array.from({ length: 7 }).map((_, idx) => {
      const date = new Date(start);
      date.setDate(start.getDate() + idx);
      return {
        key: date.toISOString().slice(0, 10),
        label: formatWithOptions(date, { weekday: 'short' }),
        day: date.getDate(),
        month: formatWithOptions(date, { month: 'short' })
      };
    });
  }, []);

  const calendarSessions = useMemo(() => {
    const normalize = (value) => value.trim().slice(0, 3).toLowerCase();
    const dayMap = {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun'
    };
    const parseDays = (value) => {
      if (!value) return [];
      return value
        .replace(/&/g, '/')
        .replace(/,/g, '/')
        .split('/')
        .map((part) => normalize(part))
        .filter(Boolean)
        .map((token) => dayMap[token])
        .filter(Boolean);
    };
    const sessions = {};
    weekDays.forEach((day) => {
      sessions[day.key] = [];
    });
    programmeRows.forEach((row) => {
      const days = row.days?.length ? row.days : parseDays(row.day);
      days.forEach((dayLabel) => {
        const match = weekDays.find((day) => day.label === dayLabel);
        if (!match) return;
        sessions[match.key].push({
          id: `${row.id}-${dayLabel}`,
          title: row.name,
          time: row.time || 'TBD',
          staff: row.staff || 'Unassigned'
        });
      });
    });
    return sessions;
  }, [programmeRows, weekDays]);

  const attentionItems = useMemo(() => {
    const now = new Date();
    const items = [];
    programmeRows.forEach((row) => {
      if (row.documentCount === 0) {
        items.push({ programme: row.name, reason: 'Missing documents', severity: 'review' });
      }
      if (row.templateCount === 0) {
        items.push({ programme: row.name, reason: 'No session templates', severity: 'review' });
      }
      if (row.lessonPlanCount === 0) {
        items.push({ programme: row.name, reason: 'No lesson plans uploaded', severity: 'review' });
      }
      if (!row.staff || row.staff === 'Unassigned') {
        items.push({ programme: row.name, reason: 'Support worker not assigned', severity: 'review' });
      }
      if (row.totalFees === 0) {
        items.push({ programme: row.name, reason: 'Payment not configured', severity: 'inactive' });
      }
      if (row.utilisation >= 90 && row.capacity > 0) {
        items.push({ programme: row.name, reason: 'At capacity', severity: 'info' });
      }
      if (row.enrolled === 0) {
        items.push({ programme: row.name, reason: 'No enrolments', severity: 'review' });
      }
      if (row.endDate) {
        const endDate = new Date(row.endDate);
        if (!Number.isNaN(endDate.getTime())) {
          const diffDays = Math.round((endDate - now) / (1000 * 60 * 60 * 24));
          if (diffDays >= 0 && diffDays <= 30) {
            items.push({
              programme: row.name,
              reason: `Ends in ${diffDays} days`,
              severity: 'alert'
            });
          }
        }
      }
    });
    const priority = { alert: 0, review: 1, info: 2, inactive: 3 };
    return items
      .sort((a, b) => (priority[a.severity] ?? 9) - (priority[b.severity] ?? 9))
      .slice(0, 6);
  }, [programmeRows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Programme Overview</h2>
          <p className="sp-muted">Course performance, capacity health, and delivery readiness.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/programmes')}>
            Programme Analytics
          </Button>
          <Button onClick={() => navigate('/programmes/overview/new')}>
            <Icons.Plus /> Add Programme
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Card className="sp-metric">
          <p className="sp-label">Programmes</p>
          <p className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{analytics.totalProgrammes}</p>
          <p className="text-xs sp-muted mt-2">Total capacity: {analytics.totalCapacity}</p>
        </Card>
        <Card className="sp-metric">
          <p className="sp-label">Enrolled</p>
          <p className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{analytics.totalEnrolled}</p>
          <p className="text-xs sp-muted mt-2">Avg utilisation: {analytics.avgUtilisation}%</p>
        </Card>
        <Card className="sp-metric">
          <p className="sp-label">At Capacity</p>
          <p className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{analytics.fullCount}</p>
          <p className="text-xs sp-muted mt-2">Low utilisation: {analytics.lowUtilisation}</p>
        </Card>
        <Card className="sp-metric">
          <p className="sp-label">Readiness Gaps</p>
          <p className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>{analytics.unassignedStaff}</p>
          <p className="text-xs sp-muted mt-2">
            Missing docs: {analytics.missingDocs} · Missing templates: {analytics.missingTemplates}
          </p>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Programme Health Watchlist</h3>
            <div className="text-xs sp-muted">Total fees logged: £{analytics.totalFees.toFixed(0)}</div>
          </div>
          {analytics.watchlist.length === 0 ? (
            <p className="text-sm sp-muted">All programmes are currently healthy.</p>
          ) : (
            <div className="space-y-3">
              {analytics.watchlist.map(({ row, reasons }) => (
                <div key={row.id} className="sp-panel sp-panel--muted p-3 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="sp-value" style={{ fontWeight: 600 }}>{row.name}</p>
                    <p className="text-xs sp-muted">
                      {row.day} · {row.time} · {row.staff || 'Unassigned'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {reasons.map((reason) => (
                      <Badge key={reason} variant="review">{reason}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Ending Soon</h3>
            <p className="text-xs sp-muted">Next 30 days</p>
          </div>
          {analytics.endingSoon.length === 0 ? (
            <p className="text-sm sp-muted">No programmes ending in the next 30 days.</p>
          ) : (
            <div className="space-y-3">
              {analytics.endingSoon.map((row) => (
                <div key={row.id} className="sp-panel sp-panel--muted p-3">
                  <p className="sp-value" style={{ fontWeight: 600 }}>{row.name}</p>
                  <p className="text-xs sp-muted">Ends {formatDate(row.endDate)}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 text-xs sp-muted">
            Empty programmes: {analytics.emptyCount}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Weekly Programme Calendar</h3>
              <p className="text-xs sp-muted">Scheduled sessions for this week.</p>
            </div>
            <Badge variant="info">{weekDays[0]?.month}</Badge>
          </div>
          <div className="sp-calendar">
            {weekDays.map((day) => (
              <div key={day.key} className="sp-calendar__day">
                <div className="sp-calendar__heading">
                  <span className="sp-label">{day.label}</span>
                  <span className="sp-value" style={{ fontWeight: 600 }}>{day.day}</span>
                </div>
                <div className="sp-calendar__slots">
                  {(calendarSessions[day.key] || []).length === 0 ? (
                    <span className="text-xs sp-muted">No sessions</span>
                  ) : (
                    calendarSessions[day.key].slice(0, 3).map((session) => (
                      <div key={session.id} className="sp-calendar__event">
                        <div className="sp-value" style={{ fontWeight: 600 }}>{session.title}</div>
                        <div className="text-xs sp-muted">{session.time}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Upcoming Attention</h3>
              <p className="text-xs sp-muted">Documents, payments, and staffing gaps.</p>
            </div>
          </div>
          {attentionItems.length === 0 ? (
            <p className="text-sm sp-muted">No outstanding actions flagged.</p>
          ) : (
            <div className="space-y-3">
              {attentionItems.map((item, index) => (
                <div key={`${item.programme}-${index}`} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="sp-value" style={{ fontWeight: 600 }}>{item.programme}</p>
                      <p className="text-xs sp-muted">{item.reason}</p>
                    </div>
                    <Badge variant={item.severity === 'alert' ? 'critical' : item.severity}>
                      {item.severity === 'alert' ? 'urgent' : item.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Programme Overview</h3>
          <Button size="sm" variant="secondary" onClick={() => navigate('/students')}>
            View enrolments
          </Button>
        </div>
        <div className="sp-table sp-table--programme-overview">
          <div className="sp-row sp-row--header">
            <div>Programme</div>
            <div>Schedule</div>
            <div>Dates</div>
            <div>Capacity</div>
            <div>Enrolled</div>
            <div>Seats Remaining</div>
            <div>Utilisation</div>
            <div>Actions</div>
          </div>
          {programmeRows.map((programme) => (
            <div key={programme.id} className="sp-row">
              <div>
                <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
                <div className="text-xs sp-muted">
                  Staff: {programme.staff || 'Unassigned'} · Location: {programme.location || 'TBD'}
                </div>
                {programme.staffAvailability && (
                  <div className="text-xs sp-muted">Availability: {programme.staffAvailability}</div>
                )}
                <div className="text-xs sp-muted">
                  Templates: {programme.templateCount} · Docs: {programme.documentCount} · Fees: £{programme.totalFees.toFixed(0)}
                </div>
              </div>
              <div className="text-xs sp-muted">{programme.day} · {programme.time}</div>
              <div className="text-xs sp-muted">
                {formatDate(programme.startDate) || 'TBD'}{programme.endDate ? ` → ${formatDate(programme.endDate)}` : ''}
              </div>
              <div className="sp-value">{programme.capacity || '—'}</div>
              <div className="sp-value">{programme.enrolled}</div>
              <div className="sp-value">{programme.seatsRemaining}</div>
              <div>
                <Badge variant={programme.utilisation >= 90 ? 'review' : programme.utilisation === 0 ? 'inactive' : 'active'}>
                  {programme.utilisation}%
                </Badge>
              </div>
              <div className="sp-actions">
              <Button size="sm" variant="secondary" onClick={() => navigate(`/students?programme=${encodeURIComponent(programme.name)}`)}>
                View students
              </Button>
                <Button size="sm" onClick={() => navigate(`/programmes/overview/${programme.id}/edit`)}>
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

    </div>
  );
}
